<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="pt-6 pb-5 space-y-6">
      <Form :initial-values="initial_values" :validation-schema="schema" class="mt-2 space-y-3" @submit="sendEmail">
        <div class="p-2 border rounded-md dark:border-dark-divider-border">
          <div class="m-1 text-lg text-common-grey">
            {{ $t("Sender") }}:
          </div>
          <div class="flex items-center gap-1 rounded-md shadow-sm">
            <SenderAutoCompleteInput
              v-model:selected-sender="selected_sender"
              :placeholder="$t('Sender')"
              :in-playbook="inPlaybook"
              input-classes="placeholder-common-grey"
              :senders="
                autocomplete_options_in_playbook_with_values_for_sender.filter((o) =>
                  o.value.id ? true : typeof o.value == 'string' && !o.value?.includes('co_owners')
                )
              " />
          </div>
          <div class="flex flex-row flex-wrap gap-2 mt-1">
            <!-- For sender -->
            <ContactCard
              v-if="selected_sender?.id"
              :key="selected_sender?.id"
              :contact="selected_sender"
              :selected-cc-contacts="selected_cc_contacts"
              :selected-bcc-contacts="selected_bcc_contacts"
              :contact-type="'recipient'"
              :ellipsis-vertical-icon="EllipsisVertical"
              :for-sender="true"
              @removeContact="handleRemoveSender"
              @moveToCc="
                ($event) => {
                  handleMoveToCc($event)
                  selected_sender = null
                }
              "
              @moveToBcc="
                ($event) => {
                  handleMoveToBcc($event)
                  selected_sender = null
                }
              " />
          </div>
        </div>
        <div class="p-2 border rounded-md dark:border-dark-divider-border">
          <div class="m-1 text-lg text-common-grey">
            {{ $t("Recipients") }}:
            <span v-if="isAllRecords"
                  class="ml-2 text-status-error-red">{{ count - excludedRecords.length }} {{ $t("out of") }} {{ count }} {{ $t("selected") }}
            </span>
          </div>
          <div class="flex items-center gap-1 rounded-md shadow-sm">
            <ContactsAutoCompleteInput
              placeholder="Send to"
              input-classes="placeholder-common-grey"
              :autocomplete-options="autocompleteOptionsInPlaybook"
              :selected-contacts="autocomplete_options_in_playbook_with_values_for_contacts"
              :selected-groups="selected_groups"
              :selected-companies="selected_companies"
              @submitted="handleAddItem"
              @chosen="handleAddItem" />
          </div>
          <div class="flex flex-row flex-wrap gap-2 mt-1">
            <!-- For recipient contacts -->
            <ContactCard
              v-for="contact in selected_contacts"
              :key="contact.id"
              :contact="contact"
              :selected-cc-contacts="selected_cc_contacts"
              :selected-bcc-contacts="selected_bcc_contacts"
              :contact-type="'recipient'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @removeContact="handleRemoveContact"
              @moveToCc="handleMoveToCc"
              @moveToBcc="handleMoveToBcc" />

            <GroupCard
              v-for="group in selected_groups"
              :key="group.id"
              :group="group"
              :selected-groups="selected_groups"
              :selected-cc-groups="selected_cc_groups"
              :selected-bcc-groups="selected_bcc_groups"
              :group-type="'recipients'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @deleteGroup="handleDeleteGroup"
              @moveToRecipients="handleMoveGroupToRecipients"
              @moveToCc="handleMoveGroupToCc"
              @moveToBcc="handleMoveGroupToBcc"
              @editContacts="handleEditContacts" />

            <CompanyCard
              v-for="company in selected_companies"
              :key="company.id"
              :company="company"
              :selected-companies="selected_companies"
              :selected-cc-companies="selected_cc_companies"
              :selected-bcc-companies="selected_bcc_companies"
              :group-type="'recipients'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @deleteCompany="handleDeleteCompany"
              @moveToRecipients="handleMoveCompanyToRecipients"
              @moveToCc="handleMoveCompanyToCc"
              @moveToBcc="handleMoveCompanyToBcc"
              @editContacts="handleEditContacts" />

            <SimpleActionDropDown
              v-if="selected_contacts.length || selected_companies.length || selected_groups.length"
              classes="relative flex-shrink-0 ml-auto top-1"
              dropdown-class="absolute z-50 w-48 mx-3 mt-1 -ml-24 origin-bottom-right bg-white rounded-md shadow-lg dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none"
              :icon="EllipsisVertical"
              item-class="text-gray-700 dark:text-dark-body-text"
              item-active-class="text-gray-400 bg-gray-100 dark:bg-dark-screen-background"
              :icon-button="true"
              :items="[
                {
                  name: $t('Move all to CC'),
                  action: () => {
                    handleMoveAllToCC()
                  }
                },
                {
                  name: $t('Move all to BCC'),
                  action: () => {
                    handleMoveAllToBCC()
                  }
                }
              ]" />
          </div>
        </div>

        <div v-if="cc_active" class="p-2 border rounded-md dark:border-dark-divider-border">
          <div class="m-1 text-lg text-common-grey">
            CC:
          </div>
          <div class="flex items-center gap-1 rounded-md shadow-sm">
            <ContactsAutoCompleteInput
              placeholder="Add"
              :autocomplete-options="autocomplete_options_in_playbook_with_values_for_cc_contacts"
              input-classes="placeholder-common-grey"
              :selected-contacts="selected_cc_contacts"
              :selected-groups="selected_cc_groups"
              :selected-companies="selected_cc_companies"
              @submitted="
                ($event) => {
                  handleAddItem($event, 'cc')
                }
              "
              @chosen="
                ($event) => {
                  handleAddItem($event, 'cc')
                }
              " />
          </div>
          <div class="flex flex-row flex-wrap gap-2 mt-1">
            <ContactCard
              v-for="contact in selected_cc_contacts"
              :key="contact.id"
              :contact="contact"
              :selected-contacts="selected_contacts"
              :selected-bcc-contacts="selected_bcc_contacts"
              :contact-type="'cc'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @removeContact="handleRemoveCcContact"
              @moveToRecipients="handleMoveToRecipients"
              @moveToBcc="handleMoveToBcc" />

            <GroupCard
              v-for="group in selected_cc_groups"
              :key="group.id"
              :group="group"
              :selected-groups="selected_groups"
              :selected-cc-groups="selected_cc_groups"
              :selected-bcc-groups="selected_bcc_groups"
              :group-type="'cc'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @deleteGroup="handleDeleteGroup"
              @moveToRecipients="handleMoveGroupToRecipients"
              @moveToCc="handleMoveGroupToCc"
              @moveToBcc="handleMoveGroupToBcc"
              @editContacts="handleEditContacts" />

            <CompanyCard
              v-for="company in selected_cc_companies"
              :key="company.id"
              :company="company"
              :selected-companies="selected_companies"
              :selected-cc-companies="selected_cc_companies"
              :selected-bcc-companies="selected_bcc_companies"
              :group-type="'cc'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @deleteCompany="handleDeleteCompany"
              @moveToRecipients="handleMoveCompanyToRecipients"
              @moveToCc="handleMoveCompanyToCc"
              @moveToBcc="handleMoveCompanyToBcc"
              @editContacts="handleEditContacts" />

            <SimpleActionDropDown
              v-if="selected_cc_contacts.length || selected_cc_companies.length || selected_cc_groups.length"
              classes="relative flex-shrink-0 ml-auto top-1"
              dropdown-class="absolute z-50 w-48 mx-3 mt-1 -ml-24 origin-bottom-right bg-white rounded-md shadow-lg dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none"
              :icon="EllipsisVertical"
              item-class="text-gray-700 dark:text-dark-body-text"
              item-active-class="text-gray-400 bg-gray-100 dark:bg-dark-screen-background"
              :icon-button="true"
              :items="[
                {
                  name: $t('Move all to Recipients'),
                  action: () => {
                    handleMoveAllToRecipients()
                  }
                },
                {
                  name: $t('Move all to BCC'),
                  action: () => {
                    handleMoveAllToBCCfromCC()
                  }
                }
              ]" />
          </div>
        </div>

        <div class="text-common-grey">
          <span v-if="!cc_active" class="mr-1 cursor-pointer" @click="cc_active = true">{{ $t("CC") }}</span><span v-if="!cc_active && !bcc_active">/</span><span v-if="!bcc_active" class="ml-1 cursor-pointer" @click="bcc_active = true">{{ $t("BCC") }}</span>
        </div>

        <div v-if="bcc_active" class="p-2 border rounded-md dark:border-dark-divider-border">
          <div class="m-1 text-lg text-common-grey">
            {{ $t("BCC") }}:
          </div>
          <div class="flex items-center gap-1 rounded-md shadow-sm">
            <ContactsAutoCompleteInput
              placeholder="Add"
              :autocomplete-options="autocomplete_options_in_playbook_with_values_for_bcc_contacts"
              input-classes="placeholder-common-grey"
              :selected-contacts="selected_bcc_contacts"
              :selected-groups="selected_bcc_groups"
              :selected-companies="selected_bcc_companies"
              @submitted="
                ($event) => {
                  handleAddItem($event, 'bcc')
                }
              "
              @chosen="
                ($event) => {
                  handleAddItem($event, 'bcc')
                }
              " />
          </div>
          <div class="flex flex-row flex-wrap gap-2 mt-1">
            <ContactCard
              v-for="contact in selected_bcc_contacts"
              :key="contact.id"
              :contact="contact"
              :selected-contacts="selected_contacts"
              :selected-bcc-contacts="selected_bcc_contacts"
              :contact-type="'bcc'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @removeContact="handleRemoveCcContact"
              @moveToRecipients="handleMoveToRecipients" />

            <GroupCard
              v-for="group in selected_bcc_groups"
              :key="group.id"
              :group="group"
              :selected-groups="selected_groups"
              :selected-cc-groups="selected_cc_groups"
              :selected-bcc-groups="selected_bcc_groups"
              :group-type="'bcc'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @deleteGroup="handleDeleteGroup"
              @moveToRecipients="handleMoveGroupToRecipients"
              @moveToCc="handleMoveGroupToCc"
              @editContacts="handleEditContacts" />

            <CompanyCard
              v-for="company in selected_bcc_companies"
              :key="company.id"
              :company="company"
              :selected-companies="selected_companies"
              :selected-cc-companies="selected_cc_companies"
              :selected-bcc-companies="selected_bcc_companies"
              :group-type="'bcc'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @deleteCompany="handleDeleteCompany"
              @moveToRecipients="handleMoveCompanyToRecipients"
              @moveToCc="handleMoveCompanyToCc"
              @editContacts="handleEditContacts" />

            <SimpleActionDropDown
              v-if="selected_bcc_contacts.length || selected_bcc_companies.length || selected_bcc_groups.length"
              classes="relative flex-shrink-0 ml-auto top-1"
              dropdown-class="absolute z-50 w-48 mx-3 mt-1 -ml-24 origin-bottom-right bg-white rounded-md shadow-lg dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none"
              :icon="EllipsisVertical"
              item-class="text-gray-700 dark:text-dark-body-text"
              item-active-class="text-gray-400 bg-gray-100 dark:bg-dark-screen-background"
              :icon-button="true"
              :items="[
                {
                  name: $t('Move all to Recipients'),
                  action: () => {
                    handleMoveAllToRecipients()
                  }
                },
                {
                  name: $t('Move all to CC'),
                  action: () => {
                    handleMoveAllToCCfromBCC()
                  }
                }
              ]" />
          </div>
        </div>

        <div class="flex items-center">
          <TextInput
            autocomplete="on"
            :autocomplete-options="resultReferencesListForText"
            :disabled="content"
            name="name"
            :value="subject"
            type="text"
            :placeholder="$t('Subject')"
            input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            @update:modelValue="
              ($event) => {
                subject = $event
              }
            " />
        </div>

        <div class="mt-2">
          <div class="relative mb-20 h-52">
            <TextEditor
              v-model="body_content"
              :value="body_content"
              :validate-on-input="true"
              content-type="html"
              :is-email-text="true"
              :mention-items="autocompleteOptionsInPlaybook.concat(resultReferencesListForText)"
              theme="snow"
              toolbar="full" />

            <div class="absolute top-1 right-12 mt-2">
              <div
                v-if="resultReferencesListForText?.length"
                :class="[resultReferencesListForText.length ? 'opacity-100' : 'opacity-0']"
                class="inset-y-0 z-10 flex items-center pr-3 transition-all duration-500 cursor-pointer"
                @click="show_autocomplete_options = !show_autocomplete_options">
                <DynamicIcon
                  :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                  class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" />
              </div>

              <transition
                v-show="resultReferencesListForText?.length && show_autocomplete_options"
                leave-active-class="transition ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0">
                <div
                  static
                  class="absolute mt-1 w-44 -ml-44 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm dark:bg-dark-box-background z-20">
                  <div v-for="item in resultReferencesListForText" :key="item.value">
                    <li
                      class="flex items-center text-gray-900 cursor-pointer hover:bg-gray-200 relative py-2 pl-2 pr-4 dark:text-white dark:hover:bg-transparent"
                      @click.prevent="handleSelectOption(item)">
                      <span class="font-normal ml-3 block"> {{ item.label }} </span>
                    </li>
                  </div>
                </div>
              </transition>
            </div>

            <SimpleActionDropDown
              v-if="!selected_companies.length && !selected_groups.length"
              classes="!absolute top-1 right-2.5"
              dropdown-class="w-[500px]"
              :items="getActions()">
              <template #button>
                <component :is="`MenuButton`" class="appearance-none">
                  <Logo :animate="is_thinking" class="transition-all appearance-none w-9 h-9 animate hover:scale-105" aria-hidden="true" />
                </component>
              </template>
              <template #footer>
                <div class="relative m-1">
                  <TextArea
                    v-model="custom_prompt"
                    :validate-on-input="true"
                    :show-counter="true"
                    name="custom_prompt"
                    :placeholder="$t('Custom? Ask away!')"
                    class="block w-full h-48 px-3 py-2" />

                  <button
                    :disabled="custom_prompt.trim().length < 5"
                    type="button"
                    class="absolute p-1 m-0 text-sm disabled:opacity-50 disabled:pointer-events-none bottom-5 right-4 w-fit primary-outline-btn"
                    @click="useCustomPrompt">
                    {{ $t("Submit") }}
                  </button>
                </div>
              </template>
            </SimpleActionDropDown>
          </div>

          <AttachmentsField v-model="attachments" overwrite-label="Assets" :options="['file']" class="w-full mt-4 mb-10" />

          <div class="relative flex flex-row justify-between w-full gap-2 mb-5 top-5" :class="{ 'relative !mt-20': inPlaybook }">
            <button type="button" class="w-1/2 m-0 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
            <button :class="{ 'pointer-events-none opacity-40': form_incomplete }" type="submit" class="w-1/2 primary-btn">
              {{ inPlaybook ? $t("Save") : $t("Send") }}
            </button>
          </div>
        </div>
      </Form>
    </div>

    <ContactsListModal
      :calc-contact-checked="calcContactChecked"
      :calc-remaining-contacts="calcRemainingContacts"
      :handle-select-contact="handleSelectContact"
      :group-or-company-contacts="group_or_company_contacts" />
  </div>
</template>

<script>
import * as Yup from "yup"

import { Form } from "vee-validate"
import ContactsAutoCompleteInput from "@/components/shared/ContactsAutoCompleteInput.vue"
import SenderAutoCompleteInput from "@/components/shared/SenderAutoCompleteInput.vue"
import { EllipsisVertical } from "lucide-vue-next"
import ContactsListModal from "./ContactsListModal.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import dataService from "@/services/data.service"
import { Record } from "@/models/record"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import SimpleActionDropDown from "@/components/shared/dropdowns/SimpleActionDropDown.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import TextArea from "@/components/shared/TextArea.vue"
import Logo from "@/components/shared/Logo.vue"
import ContactCard from "@/components/forms/email/ContactCard.vue"
import GroupCard from "@/components/forms/email/GroupCard.vue"
import CompanyCard from "@/components/forms/email/CompanyCard.vue"
import TextInput from "@/components/shared/TextInput.vue"
import DynamicIcon from "@/components/shared/icons/DynamicIcon.vue"

import json_data from "@/components/shared/json-files/all-obj-fields.json"

export default {
  components: {
    DynamicIcon,
    CompanyCard,
    GroupCard,
    ContactCard,
    TextArea,
    Logo,
    SimpleActionDropDown,
    AttachmentsField,
    Form,
    TextEditor,
    ContactsAutoCompleteInput,
    SenderAutoCompleteInput,
    TextInput,
    // eslint-disable-next-line vue/no-unused-components
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    EllipsisVertical,
    ContactsListModal
  },
  props: {
    selectedNode: null,
    inPlaybook: {
      type: Boolean,
      default: false
    },
    count: null,
    content: {
      type: String,
      required: false,
      default: null
    },
    currentObjectType: {
      type: String,
      required: false,
      default: null
    },
    record: {
      type: Object,
      required: false,
      default: null
    },
    refRecord: {
      type: Object,
      required: false,
      default: null
    },
    records: {
      type: Array,
      required: false,
      default: null
    },
    excludedRecords: {
      type: Array,
      required: false,
      default: null
    },
    isAllRecords: {
      type: Boolean,
      default: false
    },
    ccRecords: {
      type: Array,
      required: false,
      default: null
    },
    replyId: {
      type: String,
      required: false,
      default: null
    },
    threadId: {
      type: String,
      required: false,
      default: null
    },

    autocompleteOptionsInPlaybook: {
      type: Array,
      default: () => []
    },

    allLabels: null,
    resultReferencesList: null,
    resultReferencesListForText: null
  },
  emits: ["cancel", "submitted", "done", "update:body"],
  data() {
    return {
      show_autocomplete_options: false,
      show_attachments: false,
      EllipsisVertical,
      Logo,
      Record,
      is_thinking: false,
      initial_values: {},
      show_prompts: false,
      custom_prompt: "",
      subject: "",
      body_content: "",
      selected_contacts: [],
      selected_sender: null,
      preselected_sender_id: null,
      selected_companies: [],
      selected_cc_companies: [],
      selected_bcc_companies: [],
      selected_cc_contacts: [],
      selected_bcc_contacts: [],
      selected_groups: [],
      selected_cc_groups: [],
      selected_bcc_groups: [],
      cc_active: false,
      bcc_active: false,
      attachments: [],
      group_or_company_contacts: { results: [] },
      contacts_modal_group_or_company: null,
      contacts_modal_item_type: null,
      contacts_modal_list: null,
      forecasted_objects: [],
      other_metrics: []
    }
  },
  computed: {
    autocomplete_options_in_playbook_with_values_for_contacts() {
      const list_calc = []

      this.autocompleteOptionsInPlaybook.forEach((op) => {
        const found_user = this.selected_contacts.find((user) => user.id == op.id)
        if (found_user && found_user.value?.email) {
          list_calc.push({ ...op, value: found_user.value })
        } else {
          list_calc.push(op)
        }
      })
      return list_calc
    },

    autocomplete_options_in_playbook_with_values_for_cc_contacts() {
      const list_calc = []

      this.autocompleteOptionsInPlaybook.forEach((op) => {
        const found_user = this.selected_contacts.find((user) => user.id == op.id)
        if (found_user && found_user.value?.email) {
          list_calc.push({ ...op, value: found_user.value })
        } else {
          list_calc.push(op)
        }
      })
      return list_calc
    },

    autocomplete_options_in_playbook_with_values_for_bcc_contacts() {
      const list_calc = []

      this.autocompleteOptionsInPlaybook.forEach((op) => {
        const found_user = this.selected_contacts.find((user) => user.id == op.id)
        if (found_user && found_user.value?.email) {
          list_calc.push({ ...op, value: found_user.value })
        } else {
          list_calc.push(op)
        }
      })
      return list_calc
    },

    autocomplete_options_in_playbook_with_values_for_sender() {
      const list_calc = []

      this.autocompleteOptionsInPlaybook.forEach((op) => {
        const is_sender = this.selected_sender?.id == op.id
        if (is_sender && this.selected_sender.value?.email) {
          list_calc.push({ ...op, value: this.selected_sender.value })
        } else {
          list_calc.push(op)
        }
      })
      return list_calc
    },

    integrationSenderOptions() {
      return (this.$store.state.integration.integrations || [])
        .filter((i) => (i.name == "outlook" || i.name == "gmail") && i.status == "on")
        .map(this.buildIntegrationSenderOption)
        .filter(Boolean)
    },

    organizationSenderOptions() {
      return (this.$store.state.data.workspace_organizations || []).map(this.parseOrganizationSenderOption).filter(Boolean)
    },

    workspaceUserSenderOptions() {
      return (this.$store.state.data.workspace_organizations || []).map(this.buildWorkspaceUserSenderOption).filter(Boolean)
    },

    defaultSenderOption() {
      return this.buildDefaultSenderOption()
    },

    availableSenderOptions() {
      const options = []
      const seenKeys = new Set()
      const addOption = (option) => {
        if (!option) {
          return
        }
        const key = option.id || (option.email ? `email:${option.email.toLowerCase()}` : null)
        if (!key || seenKeys.has(key)) {
          return
        }
        seenKeys.add(key)
        options.push(option)
      }

      addOption(this.defaultSenderOption)
      this.organizationSenderOptions.forEach(addOption)
      this.workspaceUserSenderOptions.forEach(addOption)
      this.integrationSenderOptions.forEach(addOption)

      return options
    },

    form_incomplete() {
      let form_incomplete_calc = false
      if (!this.subject) {
        form_incomplete_calc = true
      }
      if (!this.body_content) {
        form_incomplete_calc = true
      }
      if (!this.selected_sender) {
        form_incomplete_calc = true
      }

      if (
        !this.selected_contacts.length &&
        !this.selected_cc_contacts.length &&
        !this.selected_bcc_contacts.length &&
        !this.selected_companies.length &&
        !this.selected_groups.length
      ) {
        form_incomplete_calc = true
      }

      return form_incomplete_calc
    },

    // computed prop to tell whether the user add his gmail or outlook to the platform
    is_email_account_linked() {
      return this.$store.state.integration.integrations.find((i) => (i.name == "outlook" || i.name == "gmail") && i.status == "on")
    },
    schema() {
      return Yup.object().shape({})
    }
  },
  watch: {
    body_content: {
      handler() {
        if (this.inPlaybook) {
          this.handleChange()
        }
      },
      deep: true
    },
    availableSenderOptions(options) {
      if (!this.preselected_sender_id) {
        return
      }
      const match = options.find((option) => option.id == this.preselected_sender_id)
      if (match) {
        this.selected_sender = this.$utils.clone(match)
        this.preselected_sender_id = null
      }
    }
  },

  created() {
    this.init()

    if (this.selectedNode?.data?.values?.sender) {
      const sender_value = this.selectedNode.data.values.sender
      const s_id = sender_value?.id ? sender_value.id : sender_value
      this.preselected_sender_id = s_id
      this.selected_sender = this.$store.state.data.users.find((u) => u.id == s_id)

      if (!this.selected_sender) {
        this.selected_sender = this.integrationSenderOptions.find((option) => option.id == s_id)
      }
      if (!this.selected_sender) {
        this.selected_sender = this.organizationSenderOptions.find((option) => option.id == s_id)
      }
      if (!this.selected_sender) {
        this.selected_sender = this.workspaceUserSenderOptions.find((option) => option.id == s_id)
      }
      if (this.selected_sender) {
        this.selected_sender = this.$utils.clone(this.selected_sender)
        this.preselected_sender_id = null
      }
    } else {
      this.selected_sender = this.$store.state.data.users.find((u) => u.id == this.$store.state.auth.user.id)
    }
    if (!this.selected_sender) {
      const defaultSender = this.defaultSenderOption
      if (defaultSender) {
        this.selected_sender = this.$utils.clone(defaultSender)
      } else {
        const fallback = this.buildDefaultSenderOption()
        if (fallback) {
          this.selected_sender = fallback
        }
      }
    }
    if (!this.selected_sender) {
      this.selected_sender = {
        full_name: "Revsetter",
        first_name: "Revsetter",
        email: "info@revsetter.com",
        id: "revsetter"
      }
    }
    if (this.selectedNode?.data?.values.content) {
      this.body_content = this.selectedNode.data.values.content
    }
    if (this.content) {
      if (this.replyId) {
        this.subject = `RE: ${this.$utils.getRecordName(this.refRecord)}`
      } else {
        this.body_content = `<p>---------- Forwarded message --------- <br>
      From: ${this.refRecord.meta_data.from_[0].name} ${
        this.refRecord.meta_data.from_[0].email ? ` (${this.refRecord.meta_data.from_[0].email})` : ""
      } <br>
      Date: ${this.$moment(this.refRecord.interaction_date).format(
        "DD MMM  YYYY"
      )} at ${this.$moment(this.refRecord.interaction_date).format("hh:mm A")}<br>
      Subject: ${this.$utils.getRecordName(this.refRecord)}<br>
      To: ${this.refRecord.users
        .map((u) => {
          const user = this.$store.state.data.users.find((o_u) => o_u.id == u)
          return `${user?.full_name} (${user?.email})`
        })
        .join(", ")}
      </p>
      `

        this.body_content += this.content
        this.subject = `FWD: ${this.$utils.getRecordName(this.refRecord)}`
      }
    }
    if (this.records?.length) {
      this.records.forEach((company_id) => {
        if (this.$store.state.data.records_fetched[company_id]) {
          this.handleAddItem(this.$store.state.data.records_fetched[company_id], "recipients")
        } else {
          this.$store.dispatch("data/getRecord", company_id).then((res) => {
            this.handleAddItem(res.data, "recipients")
          })
        }
      })
    } else if (this.record) {
      this.handleAddItem(this.record, "recipients")
    }

    if (this.ccRecords?.length) {
      this.ccRecords.forEach((record) => {
        this.handleAddItem(record, "cc")
        this.cc_active = true
      })
    }

    if (this.selectedNode?.data?.values?.subject) {
      const email_data = this.selectedNode.data.values

      this.subject = email_data.subject
      this.body_content = email_data.body
      this.selected_contacts = []
      this.selected_cc_contacts = []
      this.selected_bcc_contacts = []

      const active_sender_emails = this.availableSenderOptions.map((option) => this.$utils.clone(option))

      const found_sender = active_sender_emails
        .concat(this.autocompleteOptionsInPlaybook)
        .find((t) => t.id == (email_data.sender.id ? email_data.sender.id : email_data.sender))

      if (email_data.sender.email) {
        found_sender.value = {
          id: email_data.sender.id,
          email: email_data.sender.email.split(".")[1].replace("}", ""),
          first_name: email_data.sender.first_name ? email_data.sender.first_name.split(".")[1].replace("}", "") : "",
          last_name: email_data.sender.last_name ? email_data.sender.last_name.split(".")[1].replace("}", "") : ""
        }
      }

      if (found_sender) {
        this.selected_sender = found_sender
      }

      email_data.to.forEach((team_member_or_contact_id) => {
        const found_team_member = this.$store.state.data.users
          .concat(this.autocompleteOptionsInPlaybook)
          .find((t) => t.id == (team_member_or_contact_id.id ? team_member_or_contact_id.id : team_member_or_contact_id))

        if (team_member_or_contact_id.email) {
          found_team_member.value = {
            id: team_member_or_contact_id.id,
            email: team_member_or_contact_id.email.split(".")[1].replace("}", ""),
            first_name: team_member_or_contact_id.first_name ? team_member_or_contact_id.first_name.split(".")[1].replace("}", "") : "",
            last_name: team_member_or_contact_id.last_name ? team_member_or_contact_id.last_name.split(".")[1].replace("}", "") : ""
          }
        }

        if (found_team_member) {
          this.selected_contacts.push(found_team_member)
        }
      })

      email_data.cc.forEach((team_member_or_contact_id) => {
        const found_team_member = this.$store.state.data.users
          .concat(this.autocompleteOptionsInPlaybook)
          .find((t) => t.id == (team_member_or_contact_id.id ? team_member_or_contact_id.id : team_member_or_contact_id))

        if (team_member_or_contact_id.email) {
          found_team_member.value = {
            id: team_member_or_contact_id.id,
            email: team_member_or_contact_id.email.split(".")[1].replace("}", ""),
            first_name: team_member_or_contact_id.first_name ? team_member_or_contact_id.first_name.split(".")[1].replace("}", "") : "",
            last_name: team_member_or_contact_id.last_name ? team_member_or_contact_id.last_name.split(".")[1].replace("}", "") : ""
          }
        }

        if (found_team_member) {
          this.selected_cc_contacts.push(found_team_member)
        }
      })

      email_data.bcc.forEach((team_member_or_contact_id) => {
        const found_team_member = this.$store.state.data.users
          .concat(this.autocompleteOptionsInPlaybook)
          .find((t) => t.id == (team_member_or_contact_id.id ? team_member_or_contact_id.id : team_member_or_contact_id))

        if (team_member_or_contact_id.email) {
          found_team_member.value = {
            id: team_member_or_contact_id.id,
            email: team_member_or_contact_id.email.split(".")[1].replace("}", ""),
            first_name: team_member_or_contact_id.first_name ? team_member_or_contact_id.first_name.split(".")[1].replace("}", "") : "",
            last_name: team_member_or_contact_id.last_name ? team_member_or_contact_id.last_name.split(".")[1].replace("}", "") : ""
          }
        }
        if (found_team_member) {
          this.selected_bcc_contacts.push(found_team_member)
        }
      })

      email_data.companies.forEach((company_id) => {
        this.$store.dispatch("data/getRecord", company_id).then((res) => {
          this.selected_companies.push(res.data)
        })
      })

      email_data.cc_companies.forEach((company_id) => {
        this.$store.dispatch("data/getRecord", company_id).then((res) => {
          this.selected_cc_companies.push(res.data)
        })
      })

      email_data.bcc_companies.forEach((company_id) => {
        this.$store.dispatch("data/getRecord", company_id).then((res) => {
          this.selected_bcc_companies.push(res.data)
        })
      })

      email_data.groups.forEach((group_id) => {
        this.selected_groups.push(this.$store.state.data.groups.find((t) => t.id == group_id))
      })

      email_data.cc_groups.forEach((group_id) => {
        this.selected_groups.push(this.$store.state.data.cc_groups.find((t) => t.id == group_id))
      })

      email_data.bcc_groups.forEach((group_id) => {
        this.selected_groups.push(this.$store.state.data.bcc_groups.find((t) => t.id == group_id))
      })

      if (email_data.attachments.length) {
        this.show_attachments = false
      }
      let calc_attachments = []
      email_data.attachments?.forEach((attachment_id) => {
        dataService.getAttachment(attachment_id).then((res) => {
          calc_attachments.push(res.data)
          if (email_data.attachments.length == calc_attachments.length) {
            this.attachments = calc_attachments
          }
        })
      })
    }

    // Ensure cc_active and bcc_active are set correctly
    if (this.selected_cc_contacts.length || this.selected_cc_groups.length || this.selected_cc_companies.length) {
      this.cc_active = true
    }
    if (this.selected_bcc_contacts.length || this.selected_bcc_groups.length || this.selected_bcc_companies.length) {
      this.bcc_active = true
    }
  },

  methods: {
    init() {
      this.forecasted_objects = this.$utils
        .find(this.$store.state.settings.modules_settings, {
          reference_code: "renewal_base_metric"
        })
        ?.mapped_objects_metrics?.map((type_map) => {
          return this.$store.state.object_types.find((x) => x.id == type_map.object_type)?.type
        })
      Object.entries(json_data).map((item) => {
        this.other_metrics.push({ ...item[1], object_type: "company" })
      })
    },
    formatFullName(name) {
      if (!name) {
        return { first_name: "", last_name: "" }
      }
      const parts = name
        .split(/\s+/)
        .map((part) => part.trim())
        .filter(Boolean)
      const first_name = parts.shift() || ""
      const last_name = parts.join(" ")
      return { first_name, last_name }
    },
    buildIntegrationSenderOption(integration) {
      const email = integration?.user_email
      if (!email) {
        return null
      }
      let user_name = integration.user_name
      if (!user_name) {
        user_name = email.split("@")[0].replace(/[-_]/g, " ")
      }
      const { first_name, last_name } = this.formatFullName(user_name)
      return {
        full_name: user_name,
        first_name: first_name || user_name,
        last_name,
        id: integration.id,
        email,
        provider_label: integration.service_provider || integration.title || integration.name,
        logo_url: integration.logo_url,
        source: "integration"
      }
    },
    parseOrganizationSenderOption(organization) {
      if (!organization?.org_rs_email) {
        return null
      }
      const match = organization.org_rs_email.match(/^\s*(.*?)\s*<([^>]+)>\s*$/)
      let full_name = organization.name || "Revsetter"
      let email = organization.org_rs_email.trim()
      if (match) {
        full_name = match[1]?.trim() || full_name
        email = match[2]?.trim()
      } else if (email.includes("@")) {
        if (!organization.name) {
          full_name = email.split("@")[0].replace(/[-_]/g, " ")
        }
      } else {
        return null
      }
      const { first_name, last_name } = this.formatFullName(full_name)
      return {
        full_name,
        first_name: first_name || full_name,
        last_name,
        email,
        id: organization.id,
        organization_id: organization.id,
        source: "organization"
      }
    },
    buildDefaultSenderOption() {
      const orgEmail = this.$store.state.organization?.org_rs_email
      let email = null
      if (orgEmail) {
        const match = orgEmail.match(/<([^>]+)>/)
        email = match ? match[1] : orgEmail
      }
      if (!email) {
        const domain = this.$store.state.organization?.domain_name
        const localPart = domain?.split(".")?.[0] || "info"
        email = `${localPart}@revsetter.com`
      }
      const name = this.$store.state.organization?.name || email.split("@")[0].replace(/[-_]/g, " ") || "Revsetter"
      const { first_name, last_name } = this.formatFullName(name)
      return {
        full_name: name,
        first_name: first_name || name,
        last_name,
        email,
        id: "revsetter",
        organization_id: this.$store.state.organization?.id,
        source: "organization"
      }
    },
    buildWorkspaceUserSenderOption(organization) {
      const currentUserId = this.$store.state.auth.user?.id
      if (!currentUserId) {
        return null
      }
      const member = organization?.users?.find((user) => user.id == currentUserId)
      if (!member?.email) {
        return null
      }
      const full_name = member.full_name || `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email
      const { first_name, last_name } = this.formatFullName(full_name)
      return {
        full_name,
        first_name: member.first_name || first_name || full_name,
        last_name: member.last_name || last_name,
        email: member.email,
        id: `workspace-user-${organization.id}-${member.id}`,
        organization_id: organization.id,
        source: "workspace_user"
      }
    },
    getAllMetrics(objectType) {
      let forecast_metrics = []
      if (this.forecasted_objects?.includes(objectType)) {
        forecast_metrics = [
          {
            reference_name: `forecasts__forecast_status`,
            name: this.$t("Forecast Status"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["forecasted", "actual"] },
            object_type: objectType
          },
          {
            reference_name: `forecasts__forecast_type`,
            name: this.$t("Forecast Type"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["non-renewable", "renewable"] },
            object_type: objectType
          },
          {
            reference_name: `forecasts__forecast_value`,
            name: this.$t("Forecast Value"),
            data_type: "Number",
            input_type: "Number",
            object_type: objectType
          }
        ]
      }
      let owner_co_owner_filer = [
        {
          reference_name: `owner__email`,
          name: this.$t("Owner"),
          data_type: "String",
          input_type: "Text",
          object_type: objectType
        },
        {
          reference_name: `co_owners__email`,
          name: this.$t("Co-Owners"),
          data_type: "String",
          input_type: "Text",
          object_type: objectType
        }
      ]
      let tags_filter_fields = [
        {
          reference_name: `tags__name`,
          name: this.$t("Assigned tags"),
          data_type: "String",
          input_type: "Text",
          object_type: objectType
        }
      ]

      const metrics_calc = this.$store.state.metrics
        .concat(forecast_metrics)
        .concat(owner_co_owner_filer)
        .concat(this.other_metrics)
        .concat(tags_filter_fields)

      let metrics_calc_clean = []

      metrics_calc_clean = metrics_calc.filter(
        (m) =>
          !m.reference_name.includes("owner_email") &&
          !m.reference_name.includes("owner_first_name") &&
          !m.reference_name.includes("owner_last_name") &&
          !m.reference_name.includes("owner_name") &&
          m.data_type !== "Plan" &&
          m.data_type !== "Goal" &&
          m.data_type !== "Task" &&
          m.data_type !== "Interaction" &&
          m.data_type !== "Milestone"
      )

      return metrics_calc_clean
    },

    getDynamicSender(sender) {
      const dynamic_autocomplete_options = this.autocompleteOptionsInPlaybook.filter((op) => op.dynamic_metric_selection)
      let found_dynamic_option = null
      dynamic_autocomplete_options.forEach((dynamic_option) => {
        const metric_ref_name = sender.split(".")[sender.split(".").length - 1].replace("}", "")

        const dynamic_option_calc = this.$utils.clone(dynamic_option)
        dynamic_option_calc["id"] = dynamic_option_calc["id"].replace("dynamic_metric", metric_ref_name)

        if (dynamic_option_calc["id"] == sender) {
          dynamic_option_calc["value"] = dynamic_option_calc["value"].replace("dynamic_metric", metric_ref_name)
          dynamic_option_calc["label"] =
            dynamic_option_calc["label"] +
            " " +
            this.getAllMetrics(dynamic_option_calc.object_type).find((m) => m.reference_name == metric_ref_name).name
          found_dynamic_option = dynamic_option_calc
        }
      })

      return found_dynamic_option
    },
    handleSelectOption(item) {
      this.body_content = this.$utils.replaceLast(this.body_content, "</p>", "") + `<span> ${item.value} </span> </p>`
      this.show_autocomplete_options = false
    },

    handleRemoveContact(contact) {
      // Logic to remove a contact from the selected contacts list
      const index = this.selected_contacts.findIndex((c) => c.id === contact.id)
      if (index > -1) {
        this.selected_contacts.splice(index, 1)
      }
    },
    handleRemoveSender() {
      this.selected_sender = null
    },
    handleMoveToCc(contact) {
      // Logic to move a contact to the CC list
      this.selected_cc_contacts.push(contact)
      this.handleRemoveContact(contact) // Remove from the current list if necessary
      this.cc_active = true
    },
    handleMoveToBcc(contact) {
      // Logic to move a contact to the BCC list
      this.selected_bcc_contacts.push(contact)
      this.handleRemoveContact(contact) // Remove from the current list if necessary
      this.bcc_active = true
    },
    handleMoveToRecipients(contact) {
      // Logic to move a contact from CC or BCC back to the recipients list
      this.selected_contacts.push(contact)
      // Remove from the CC list if it's there
      this.handleRemoveCcContact(contact)
      // Remove from the BCC list if it's there
      this.handleRemoveBccContact(contact)
    },
    handleRemoveCcContact(contact) {
      // Logic to remove a contact from the CC list
      const index = this.selected_cc_contacts.findIndex((c) => c.id === contact.id)
      if (index > -1) {
        this.selected_cc_contacts.splice(index, 1)
      }
    },
    handleRemoveBccContact(contact) {
      // Logic to remove a contact from the BCC list
      const index = this.selected_bcc_contacts.findIndex((c) => c.id === contact.id)
      if (index > -1) {
        this.selected_bcc_contacts.splice(index, 1)
      }
    },
    handleDeleteCompany(company) {
      // Remove the company from the recipients list
      this.selected_companies = this.selected_companies.filter((c) => c.id !== company.id)
    },
    handleDeleteCcCompany(company) {
      // Remove the company from the CC list
      this.selected_cc_companies = this.selected_cc_companies.filter((c) => c.id !== company.id)
    },
    handleDeleteBccCompany(company) {
      // Remove the company from the BCC list
      this.selected_bcc_companies = this.selected_bcc_companies.filter((c) => c.id !== company.id)
    },
    handleMoveCompanyToRecipients(company) {
      // Add the company to the recipients list and remove from others
      this.selected_companies.push(company)
      this.handleDeleteCcCompany(company)
      this.handleDeleteBccCompany(company)
    },
    handleMoveCompanyToCc(company) {
      // Add the company to the CC list and remove from others
      this.selected_cc_companies.push(company)
      this.handleDeleteCompany(company)
      this.handleDeleteBccCompany(company)
      this.cc_active = true
    },
    handleMoveCompanyToBcc(company) {
      // Add the company to the BCC list and remove from others
      this.selected_bcc_companies.push(company)
      this.handleDeleteCompany(company)
      this.handleDeleteCcCompany(company)
      this.bcc_active = true
    },

    getActions() {
      const prompts = {
        "follow-up": this.$t("Please generate a follow-up email for a client who recently purchased our product."),
        introduction: this.$t("Create an introduction email for new subscribers to our newsletter."),
        "issue-resolution": this.$t("Draft an email template to address customer complaints about shipping delays."),
        promotion: this.$t("Compose a promotional email for our upcoming Black Friday sale."),
        "post-meeting": this.$t(
          "Draft an email to follow up with a client after an initial meeting, summarizing the discussion points and proposing the next steps."
        ),
        "engagement-nudge": this.$t(
          "I need to send an email to a prospect who has shown interest in our services but hasn't responded to our last communication."
        ),
        appreciation: this.$t(
          "Compose a thank-you email to a client who recently completed a successful project with us, expressing appreciation and asking for feedback."
        ),
        "update-notification": this.$t(
          "Write an email to inform clients about an upcoming update to our software that will require some downtime."
        ),
        "re-engagement": this.$t("Generate a re-engagement email for clients who haven't interacted with our platform in over six months."),
        "loyalty-discount": this.$t(
          "Send out an email offering a special discount to our long-term clients as a token of our appreciation."
        ),
        "new-feature": this.$t(
          "Craft an email to notify clients about a new feature release that could benefit their business operations."
        ),
        "crm-introduction": this.$t(
          "I want to introduce myself to a new contact who's been added to my CRM and schedule a virtual meeting."
        ),
        "cold-lead": this.$t(
          "Prepare an email to reach out to a list of cold leads, providing information about our product and its advantages."
        ),
        "service-apology": this.$t(
          "Create an apology email for a service outage that affected some of our clients, including preventative measures for the future."
        ),
        "demo-confirmation": this.$t(
          "Write an email to confirm a scheduled demo with a potential client, including the agenda and log-in details for the virtual meeting."
        ),
        "testimonial-request": this.$t("Draft an email to request a testimonial or case study participation from a satisfied client."),
        "holiday-greetings": this.$t(
          "Develop an email for the holiday season, wishing clients well and informing them of any special hours or services available during the period."
        )
      }

      let actions = [
        {
          name: this.$t("Follow-Up Email"),
          prompt: prompts["follow-up"],
          action: () => this.sendPrompt(prompts["follow-up"])
        },
        {
          name: this.$t("Introduction Email"),
          prompt: prompts.introduction,
          action: () => this.sendPrompt(prompts.introduction)
        },
        {
          name: this.$t("Issue Resolution Email"),
          prompt: prompts["issue-resolution"],
          action: () => this.sendPrompt(prompts["issue-resolution"])
        },
        {
          name: this.$t("Promotion Email"),
          prompt: prompts.promotion,
          action: () => this.sendPrompt(prompts.promotion)
        },
        {
          name: this.$t("Post-Meeting Follow-Up"),
          prompt: prompts["post-meeting"],
          action: () => this.sendPrompt(prompts["post-meeting"])
        },
        {
          name: this.$t("Engagement Nudge Email"),
          prompt: prompts["engagement-nudge"],
          action: () => this.sendPrompt(prompts["engagement-nudge"])
        }
        // {
        //   name: "Appreciation Email",
        //   prompt: prompts["appreciation"],
        //   action: () => this.sendPrompt(prompts["appreciation"])
        // },
        // {
        //   name: "Update Notification Email",
        //   prompt: prompts["update-notification"],
        //   action: () => this.sendPrompt(prompts["update-notification"])
        // },
        // {
        //   name: "Re-engagement Email",
        //   prompt: prompts["re-engagement"],
        //   action: () => this.sendPrompt(prompts["re-engagement"])
        // },
        // {
        //   name: "Loyalty Discount Email",
        //   prompt: prompts["loyalty-discount"],
        //   action: () => this.sendPrompt(prompts["loyalty-discount"])
        // },
        // {
        //   name: "New Feature Announcement Email",
        //   prompt: prompts["new-feature"],
        //   action: () => this.sendPrompt(prompts["new-feature"])
        // },
        // {
        //   name: "CRM Introduction Email",
        //   prompt: prompts["crm-introduction"],
        //   action: () => this.sendPrompt(prompts["crm-introduction"])
        // },
        // {
        //   name: "Cold Lead Introduction Email",
        //   prompt: prompts["cold-lead"],
        //   action: () => this.sendPrompt(prompts["cold-lead"])
        // },
        // {
        //   name: "Service Apology Email",
        //   prompt: prompts["service-apology"],
        //   action: () => this.sendPrompt(prompts["service-apology"])
        // },
        // {
        //   name: "Demo Confirmation Email",
        //   prompt: prompts["demo-confirmation"],
        //   action: () => this.sendPrompt(prompts["demo-confirmation"])
        // },
        // {
        //   name: "Testimonial Request Email",
        //   prompt: prompts["testimonial-request"],
        //   action: () => this.sendPrompt(prompts["testimonial-request"])
        // },
        // {
        //   name: "Holiday Greetings Email",
        //   prompt: prompts["holiday-greetings"],
        //   action: () => this.sendPrompt(prompts["holiday-greetings"])
        // }
      ]

      return actions
    },

    togglePromptOptions() {
      this.show_prompts = !this.show_prompts
    },
    useCustomPrompt() {
      // Enhance custom prompt with JSON format instructions
      const enhancedPrompt =
        this.custom_prompt +
        " Please format the response as JSON with 'subject' and 'body' fields. Use HTML formatting for the body content."
      this.sendPrompt(enhancedPrompt)
    },
    sendPrompt(prompt) {
      this.is_thinking = true
      let context_messages = this.getPromptContextMessages()

      // Enhanced request with options for better email generation
      const requestData = {
        prompt: prompt,
        context_messages: context_messages,
        options: {
          response_length: "medium",
          creativity_level: "medium",
          temperature: 0.5
        },
        output_format: {
          schema: {
            type: "object",
            properties: {
              subject: { type: "string", description: "Email subject line" },
              body: { type: "string", description: "Email body content in HTML format" }
            },
            required: ["subject", "body"]
          }
        }
      }

      dataService
        .post("/api/ai/chat/", requestData)
        .then((res) => {
          this.is_thinking = false
          // the result will come as structured JSON with subject and body
          let content = res.data.response
          this.subject = content.subject
          this.body_content = content.body
        })
        .catch((error) => {
          this.is_thinking = false
          console.error("Error generating email content:", error)
          // Fallback to basic prompt if structured request fails
          dataService.prompt(prompt, context_messages).then((res) => {
            let content = res.data.response
            if (typeof content === "object" && content.subject && content.body) {
              this.subject = content.subject
              this.body_content = content.body
            } else if (typeof content === "object" && content.message) {
              // Try to parse the message field
              try {
                let parsed = JSON.parse(content.message)
                this.subject = parsed.subject
                this.body_content = parsed.body
              } catch (e) {
                // If parsing fails, use the message as body
                this.body_content = content.message
                this.subject = "Generated Email"
              }
            } else {
              this.body_content = content
              this.subject = "Generated Email"
            }
          })
        })
      this.custom_prompt = ""
      this.show_prompts = false
    },
    getPromptContextMessages() {
      // provide info about the sender and the recipient
      // Sender name,Sender company name,Recipients names,Recipients company name
      let sender_name = this.$store.state.auth.user.full_name
      let sender_company_name = this.$store.state.organization.name
      let recipients_company_name = null
      // extract recipient name from the selected contacts
      let recipients_names = []
      if (this.selected_contacts.length) {
        this.selected_contacts.forEach((c) => {
          recipients_names.push(c.name || c.full_name)
        })
      } else if (this.selected_companies.length) {
        recipients_company_name = this.selected_companies[0].name
        this.getCompanyContacts(this.selected_companies[0]).forEach((c) => {
          recipients_names.push(c.name)
        })
      } else if (this.selected_groups.length) {
        this.getGroupContacts(this.selected_groups[0]).forEach((c) => {
          recipients_names.push(c.name)
          recipients_company_name = c?.parent_name
        })
      }

      let context_message = ""

      context_message += this.$t("Sender name") + `: ${sender_name}`

      if (sender_company_name) {
        context_message += ", " + this.$t("Sender company name") + `: ${sender_company_name}`
      }
      if (recipients_names.length) {
        context_message += ", " + this.$t("Recipients names") + `: ${recipients_names.join(", ")}`
      }
      if (recipients_company_name) {
        context_message += ", " + this.$t("Recipients company name") + `: ${recipients_company_name}`
      }
      return [context_message]
    },
    handleMoveAllToCC() {
      this.selected_contacts.forEach((c) => {
        if (!this.selected_cc_contacts.find((cc_c) => cc_c.id == c.id)) {
          this.selected_cc_contacts.push(c)
        }
      })
      this.selected_companies.forEach((c) => {
        if (!this.selected_cc_companies.find((cc_c) => cc_c.id == c.id)) {
          this.selected_cc_companies.push(c)
        }
      })
      this.selected_groups.forEach((g) => {
        if (!this.selected_cc_groups.find((cc_g) => cc_g.id == g.id)) {
          this.selected_cc_groups.push(g)
        }
      })
      this.selected_contacts = []
      this.selected_groups = []
      this.selected_companies = []
      this.cc_active = true
    },

    handleMoveAllToCCfromBCC() {
      this.selected_bcc_contacts.forEach((c) => {
        if (!this.selected_cc_contacts.find((cc_c) => cc_c.id == c.id)) {
          this.selected_cc_contacts.push(c)
        }
      })
      this.selected_bcc_companies.forEach((c) => {
        if (!this.selected_cc_companies.find((cc_c) => cc_c.id == c.id)) {
          this.selected_cc_companies.push(c)
        }
      })
      this.selected_bcc_groups.forEach((g) => {
        if (!this.selected_cc_groups.find((cc_g) => cc_g.id == g.id)) {
          this.selected_cc_groups.push(g)
        }
      })
      this.selected_bcc_contacts = []
      this.selected_bcc_groups = []
      this.selected_bcc_companies = []
      this.cc_active = true
    },

    handleMoveAllToRecipients() {
      this.selected_bcc_contacts.forEach((c) => {
        if (!this.selected_contacts.find((r_c) => r_c.id == c.id)) {
          this.selected_contacts.push(c)
        }
      })
      this.selected_bcc_companies.forEach((c) => {
        if (!this.selected_companies.find((r_c) => r_c.id == c.id)) {
          this.selected_companies.push(c)
        }
      })
      this.selected_bcc_groups.forEach((g) => {
        if (!this.selected_groups.find((r_g) => r_g.id == g.id)) {
          this.selected_groups.push(g)
        }
      })
      this.selected_bcc_contacts = []
      this.selected_bcc_groups = []
      this.selected_bcc_companies = []
    },

    handleMoveAllToBCC() {
      this.selected_contacts.forEach((c) => {
        if (!this.selected_bcc_contacts.find((bcc_c) => bcc_c.id == c.id)) {
          this.selected_bcc_contacts.push(c)
        }
      })
      this.selected_companies.forEach((c) => {
        if (!this.selected_bcc_companies.find((bcc_c) => bcc_c.id == c.id)) {
          this.selected_bcc_companies.push(c)
        }
      })
      this.selected_groups.forEach((g) => {
        if (!this.selected_bcc_groups.find((bcc_g) => bcc_g.id == g.id)) {
          this.selected_bcc_groups.push(g)
        }
      })
      this.selected_contacts = []
      this.selected_groups = []
      this.selected_companies = []
      this.bcc_active = true
    },

    handleMoveAllToBCCfromCC() {
      this.selected_cc_contacts.forEach((c) => {
        if (!this.selected_bcc_contacts.find((bcc_c) => bcc_c.id == c.id)) {
          this.selected_bcc_contacts.push(c)
        }
      })
      this.selected_cc_companies.forEach((c) => {
        if (!this.selected_bcc_companies.find((bcc_c) => bcc_c.id == c.id)) {
          this.selected_bcc_companies.push(c)
        }
      })
      this.selected_cc_groups.forEach((g) => {
        if (!this.selected_bcc_groups.find((bcc_g) => bcc_g.id == g.id)) {
          this.selected_bcc_groups.push(g)
        }
      })
      this.selected_cc_contacts = []
      this.selected_cc_groups = []
      this.selected_cc_companies = []
      this.bcc_active = true
    },
    handleChange() {
      const emailDataPromise = this.getEmailData()
      if (emailDataPromise instanceof Promise) {
        emailDataPromise.then((data) => {
          this.$emit("change", data)
        })
      } else {
        this.$emit("change", emailDataPromise)
      }
    },
    getEmailData() {
      const all_excluded_contacts = []
      this.selected_companies.forEach((company) => {
        if (company.unselected_contacts.length) {
          company.unselected_contacts.forEach((contact) => {
            if (!this.selected_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_contacts.push(contact)
            }
          })
        }
      })
      this.selected_groups.forEach((group) => {
        if (group.unselected_contacts.length) {
          group.unselected_contacts.forEach((contact) => {
            if (!this.selected_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_contacts.push(contact)
            }
          })
        }
      })

      const all_excluded_cc_contacts = []
      this.selected_cc_companies.forEach((company) => {
        if (company.unselected_contacts.length) {
          company.unselected_contacts.forEach((contact) => {
            if (!this.selected_cc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_cc_contacts.push(contact)
            }
          })
        }
      })
      this.selected_cc_groups.forEach((group) => {
        if (group.unselected_contacts.length) {
          group.unselected_contacts.forEach((contact) => {
            if (!this.selected_cc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_cc_contacts.push(contact)
            }
          })
        }
      })

      const all_excluded_bcc_contacts = []
      this.selected_bcc_companies.forEach((company) => {
        if (company.unselected_contacts.length) {
          company.unselected_contacts.forEach((contact) => {
            if (!this.selected_bcc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_bcc_contacts.push(contact)
            }
          })
        }
      })
      this.selected_bcc_groups.forEach((group) => {
        if (group.unselected_contacts.length) {
          group.unselected_contacts.forEach((contact) => {
            if (!this.selected_bcc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_bcc_contacts.push(contact)
            }
          })
        }
      })

      let formData = new FormData()

      let data = {
        to: this.selected_contacts.map((c) => {
          if (this.inPlaybook) {
            if (c.value?.email) {
              const result_ref = c.id.split(".")[0].replace("${", "")
              return {
                id: c.id,
                email: `$\{${result_ref}.${c.value.email}}`,
                first_name: c.value.first_name ? `$\{${result_ref}.${c.value.first_name}}` : "",
                last_name: c.value.first_name ? `$\{${result_ref}.${c.value.last_name}}` : ""
              }
            } else {
              return c.id
            }
          } else {
            return this.$utils.pick(Record.from(c), ["first_name", "last_name", "name", "email", "id"])
          }
        }),
        cc: this.selected_cc_contacts.map((c) => {
          if (this.inPlaybook) {
            if (c.value?.email) {
              const result_ref = c.id.split(".")[0].replace("${", "")
              return {
                id: c.id,
                email: `$\{${result_ref}.${c.value.email}}`,
                first_name: c.value.first_name ? `$\{${result_ref}.${c.value.first_name}}` : "",
                last_name: c.value.first_name ? `$\{${result_ref}.${c.value.last_name}}` : ""
              }
            } else {
              return c.id
            }
          } else {
            return this.$utils.pick(Record.from(c), ["first_name", "last_name", "name", "email", "id"])
          }
        }),
        bcc: this.selected_bcc_contacts.map((c) => {
          if (this.inPlaybook) {
            if (c.value?.email) {
              const result_ref = c.id.split(".")[0].replace("${", "")
              return {
                id: c.id,
                email: `$\{${result_ref}.${c.value.email}}`,
                first_name: c.value.first_name ? `$\{${result_ref}.${c.value.first_name}}` : "",
                last_name: c.value.first_name ? `$\{${result_ref}.${c.value.last_name}}` : ""
              }
            } else {
              return c.id
            }
          } else {
            return this.$utils.pick(Record.from(c), ["first_name", "last_name", "name", "email", "id"])
          }
        }),
        sender: [this.selected_sender].map((sender) => {
          if (sender?.value?.email) {
            const result_ref = sender.id.split(".")[0].replace("${", "")
            return {
              id: sender.id,
              email: `$\{${result_ref}.${sender.value.email}}`,
              first_name: sender.value.first_name ? `$\{${result_ref}.${sender.value.first_name}}` : "",
              last_name: sender.value.first_name ? `$\{${result_ref}.${sender.value.last_name}}` : ""
            }
          } else if (sender?.source === "workspace_user") {
            return this.$utils.pick(sender, ["id", "email", "first_name", "last_name", "full_name", "organization_id", "source"])
          } else {
            return sender?.id
          }
        })[0],
        groups: this.selected_groups.map((g) => g.id),
        cc_groups: this.selected_cc_groups.map((g) => g.id),
        bcc_groups: this.selected_bcc_groups.map((g) => g.id),
        companies: this.selected_companies.map((c) => c.id),
        excluded_records: this.excludedRecords,
        object_type: this.currentObjectType,
        is_all_records: this.isAllRecords,
        cc_companies: this.selected_cc_companies.map((c) => c.id),
        bcc_companies: this.selected_bcc_companies.map((c) => c.id),
        subject: this.subject,
        body: this.body_content,
        excluded_contacts: this.$utils.uniq(all_excluded_contacts),
        excluded_cc_contacts: this.$utils.uniq(all_excluded_cc_contacts),
        excluded_bcc_contacts: this.$utils.uniq(all_excluded_bcc_contacts),
        is_reply: this.replyId ? true : false,
        reply_to_message_id: this.replyId,
        thread_id: this.threadId
      }

      if (this.inPlaybook) {
        delete data.is_reply
        delete data.reply_to_message_id
        delete data.thread_id

        const promises = []
        let all_attachment_ids = []
        if (this.attachments?.length) {
          const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
          const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

          promises.push(
            dataService.addAttachment({ attachments: new_attachments }).then((res) => {
              all_attachment_ids = all_attachment_ids.concat(res.data.map((a) => a.id))
            })
          )
          all_attachment_ids = all_attachment_ids.concat(old_attachments.map((a) => a.id))

          // Return the promise so caller can wait for it to complete
          return Promise.all(promises).then(() => {
            data["attachments"] = all_attachment_ids
            return data
          })
        } else {
          return data
        }
      } else {
        formData.append("data", JSON.stringify(data))
        this.attachments.forEach((attachment, i) => {
          Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
        })

        return formData
      }
    },
    sendEmail() {
      const all_excluded_contacts = []
      this.selected_companies.forEach((company) => {
        if (company.unselected_contacts.length) {
          company.unselected_contacts.forEach((contact) => {
            if (!this.selected_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_contacts.push(contact)
            }
          })
        }
      })
      this.selected_groups.forEach((group) => {
        if (group.unselected_contacts.length) {
          group.unselected_contacts.forEach((contact) => {
            if (!this.selected_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_contacts.push(contact)
            }
          })
        }
      })

      const all_excluded_cc_contacts = []
      this.selected_cc_companies.forEach((company) => {
        if (company.unselected_contacts.length) {
          company.unselected_contacts.forEach((contact) => {
            if (!this.selected_cc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_cc_contacts.push(contact)
            }
          })
        }
      })
      this.selected_cc_groups.forEach((group) => {
        if (group.unselected_contacts.length) {
          group.unselected_contacts.forEach((contact) => {
            if (!this.selected_cc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_cc_contacts.push(contact)
            }
          })
        }
      })

      const all_excluded_bcc_contacts = []
      this.selected_bcc_companies.forEach((company) => {
        if (company.unselected_contacts.length) {
          company.unselected_contacts.forEach((contact) => {
            if (!this.selected_bcc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_bcc_contacts.push(contact)
            }
          })
        }
      })
      this.selected_bcc_groups.forEach((group) => {
        if (group.unselected_contacts.length) {
          group.unselected_contacts.forEach((contact) => {
            if (!this.selected_bcc_contacts.map((c) => c.id).includes(contact)) {
              all_excluded_bcc_contacts.push(contact)
            }
          })
        }
      })

      let formData = new FormData()

      let data = {
        to: this.selected_contacts.map((c) => {
          if (this.inPlaybook) {
            if (c.value?.email) {
              const result_ref = c.id.split(".")[0].replace("${", "")
              return {
                id: c.id,
                email: `$\{${result_ref}.${c.value.email}}`,
                first_name: c.value.first_name ? `$\{${result_ref}.${c.value.first_name}}` : "",
                last_name: c.value.first_name ? `$\{${result_ref}.${c.value.last_name}}` : ""
              }
            } else {
              return c.id
            }
          } else {
            return this.$utils.pick(Record.from(c), ["first_name", "last_name", "name", "email", "id"])
          }
        }),
        cc: this.selected_cc_contacts.map((c) => {
          if (this.inPlaybook) {
            if (c.value?.email) {
              const result_ref = c.id.split(".")[0].replace("${", "")
              return {
                id: c.id,
                email: `$\{${result_ref}.${c.value.email}}`,
                first_name: c.value.first_name ? `$\{${result_ref}.${c.value.first_name}}` : "",
                last_name: c.value.first_name ? `$\{${result_ref}.${c.value.last_name}}` : ""
              }
            } else {
              return c.id
            }
          } else {
            return this.$utils.pick(Record.from(c), ["first_name", "last_name", "name", "email", "id"])
          }
        }),
        bcc: this.selected_bcc_contacts.map((c) => {
          if (this.inPlaybook) {
            if (c.value?.email) {
              const result_ref = c.id.split(".")[0].replace("${", "")
              return {
                id: c.id,
                email: `$\{${result_ref}.${c.value.email}}`,
                first_name: c.value.first_name ? `$\{${result_ref}.${c.value.first_name}}` : "",
                last_name: c.value.first_name ? `$\{${result_ref}.${c.value.last_name}}` : ""
              }
            } else {
              return c.id
            }
          } else {
            return this.$utils.pick(Record.from(c), ["first_name", "last_name", "name", "email", "id"])
          }
        }),
        sender: [this.selected_sender].map((sender) => {
          if (sender.value?.email) {
            const result_ref = sender.id.split(".")[0].replace("${", "")
            return {
              id: sender.id,
              email: `$\{${result_ref}.${sender.value.email}}`,
              first_name: sender.value.first_name ? `$\{${result_ref}.${sender.value.first_name}}` : "",
              last_name: sender.value.first_name ? `$\{${result_ref}.${sender.value.last_name}}` : ""
            }
          } else if (sender?.source === "workspace_user") {
            return this.$utils.pick(sender, ["id", "email", "first_name", "last_name", "full_name", "organization_id", "source"])
          }
          return sender.id
        })[0],
        groups: this.selected_groups.map((g) => g.id),
        cc_groups: this.selected_cc_groups.map((g) => g.id),
        bcc_groups: this.selected_bcc_groups.map((g) => g.id),
        companies: this.selected_companies.map((c) => c.id),
        excluded_records: this.excludedRecords,
        object_type: this.currentObjectType,
        is_all_records: this.isAllRecords,
        cc_companies: this.selected_cc_companies.map((c) => c.id),
        bcc_companies: this.selected_bcc_companies.map((c) => c.id),
        subject: this.subject,
        body: this.body_content,
        excluded_contacts: this.$utils.uniq(all_excluded_contacts),
        excluded_cc_contacts: this.$utils.uniq(all_excluded_cc_contacts),
        excluded_bcc_contacts: this.$utils.uniq(all_excluded_bcc_contacts),
        is_reply: this.replyId ? true : false,
        reply_to_message_id: this.replyId,
        thread_id: this.threadId
      }

      if (this.inPlaybook) {
        delete data.is_reply
        delete data.reply_to_message_id
        delete data.thread_id

        const promises = []
        let all_attachment_ids = []
        if (this.attachments?.length) {
          const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
          const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

          promises.push(
            dataService.addAttachment({ attachments: new_attachments }).then((res) => {
              all_attachment_ids = all_attachment_ids.concat(res.data.map((a) => a.id))
            })
          )
          all_attachment_ids = all_attachment_ids.concat(old_attachments.map((a) => a.id))

          // Wait for all promises to complete before emitting done event
          Promise.all(promises).then(() => {
            data["attachments"] = all_attachment_ids
            this.$emit("done", data)
          })
        } else {
          this.$emit("done", data)
        }
      } else {
        formData.append("data", JSON.stringify(data))
        this.attachments.forEach((attachment, i) => {
          Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
        })

        if (this.is_email_account_linked) {
          dataService.sendEmail(formData).then(() => {
            this.$emit("done")
          })
        } else {
          this.$toast.open({
            message: this.$t("Please link your email account first"),
            position: "top",
            type: "error",
            duration: 3000
          })
        }
      }
    },
    calcRemainingContacts() {
      return this[
        `selected_${
          this.contacts_modal_list === "cc" ? "cc_" : this.contacts_modal_list === "bcc" ? "bcc_" : ""
        }${this.contacts_modal_item_type}`
      ].find((c) => c.id == this.contacts_modal_group_or_company.id).unselected_contacts.length
    },

    calcContactChecked(contact) {
      return !this[
        `selected_${
          this.contacts_modal_list === "cc" ? "cc_" : this.contacts_modal_list === "bcc" ? "bcc_" : ""
        }${this.contacts_modal_item_type}`
      ]
        .find((c) => c.id == this.contacts_modal_group_or_company.id)
        .unselected_contacts.includes(contact.id)
    },
    handleSelectContact(e, contact) {
      const selected_list =
        this.contacts_modal_list === "recipients"
          ? this[`selected_${this.contacts_modal_item_type}`]
          : this.contacts_modal_list === "cc"
            ? this[`selected_cc_${this.contacts_modal_item_type}`]
            : this[`selected_bcc_${this.contacts_modal_item_type}`]
      if (e.target.checked) {
        const group_or_company_index = selected_list.findIndex((c) => c.id === this.contacts_modal_group_or_company.id)
        const unselected_contacts_array = selected_list[group_or_company_index].unselected_contacts
        const contact_index = unselected_contacts_array.findIndex((c) => c === contact.id)
        if (contact_index !== -1) {
          unselected_contacts_array.splice(contact_index, 1)
        }
        selected_list[group_or_company_index].unselected_contacts = unselected_contacts_array
      } else {
        const group_or_company_index = selected_list.findIndex((c) => c.id === this.contacts_modal_group_or_company.id)
        const unselected_contacts_array = selected_list[group_or_company_index].unselected_contacts
        const contact_index = unselected_contacts_array.findIndex((c) => c === contact.id)
        if (contact_index === -1) {
          unselected_contacts_array.push(contact.id)
        }
        selected_list[group_or_company_index].unselected_contacts = unselected_contacts_array
      }
    },
    handleEditContacts(list_name, group_or_company, is_group) {
      this.$emitter.emit(ALL_EMITTERS.OPEN_EMAIL_CONTACTS_MODAL_FROM_SEND_EMAIL_FORM)
      this.contacts_modal_group_or_company = group_or_company
      this.contacts_modal_list = list_name
      if (is_group) {
        this.contacts_modal_item_type = "groups"
        this.group_or_company_contacts = this.getGroupContacts(group_or_company)
      } else {
        this.contacts_modal_item_type = "companies"
        this.group_or_company_contacts = this.getCompanyContacts(group_or_company)
      }
    },
    getCompanyContacts(company) {
      dataService
        .getRecords({ type: "contact", parent: company.id, page_size: 100 })
        .then((result) => {
          return result.data
        })
        .catch(() => {
          return []
        })
    },
    getGroupContacts(group) {
      dataService
        .getContactsForGroup(group.id, { page_size: 100 })
        .then((result) => {
          return result.data
        })
        .catch(() => {
          return []
        })
    },
    handleAddSender(sender) {
      this.selected_sender == sender
    },
    handleAddItem(data, list_name) {
      if (data.type === "results") {
        this["selected_contacts"].push(data.item)
      } else if (data.type === "contacts" || data.type === "contact" || data.type === "team_members") {
        this[`selected_${list_name == "cc" ? "cc_" : list_name == "bcc" ? "bcc_" : ""}contacts`].push(data.item ? data.item : data)
      } else if (data.type === "groups") {
        let params = { page_size: 100 }
        let total_contacts = 0
        dataService.getContactsForGroup(data.item.id, params).then((result) => {
          total_contacts = result.data.count

          if (total_contacts) {
            this[`selected_${list_name == "cc" ? "cc_" : list_name == "bcc" ? "bcc_" : ""}groups`].push({
              ...data.item,
              unselected_contacts: [],
              contacts_count: total_contacts
            })
          } else {
            this.$toast.open({
              message: this.$t("Group selected doesn't have any contacts, so can't be added to list"),
              position: "top",
              type: "error",
              duration: 3000
            })
          }
        })
      } else if (data.type === "companies" || data.type === "company") {
        let params = {
          type: "contact",
          parent: data.id ? data.id : data.item.id,
          page_size: 1
        }
        let total_contacts = 0
        dataService.getRecords(params).then((result) => {
          total_contacts = result.data.count
          if (total_contacts) {
            this[`selected_${list_name == "cc" ? "cc_" : list_name == "bcc" ? "bcc_" : ""}companies`].push({
              ...(data.item ? data.item : data),
              unselected_contacts: [],
              contacts_count: total_contacts
            })
          } else {
            this.$toast.open({
              message: this.$t("Company selected doesn't have any contacts, so can't be added to list"),
              position: "top",
              type: "error",
              duration: 3000
            })
          }
        })
      }
    },

    handleDeleteGroup(group) {
      // Logic to delete the group
      const index = this.selected_groups.findIndex((g) => g.id === group.id)
      if (index > -1) {
        this.selected_groups.splice(index, 1)
      }
    },
    handleMoveGroupToRecipients(group) {
      // Logic to move the group to recipients list
      this.removeGroupFromAllLists(group)
      this.selected_groups.push(group)
    },
    handleMoveGroupToCc(group) {
      // Logic to move the group to CC list
      this.removeGroupFromAllLists(group)
      this.selected_cc_groups.push(group)
      this.cc_active = true
    },
    handleMoveGroupToBcc(group) {
      // Logic to move the group to BCC list
      this.removeGroupFromAllLists(group)
      this.selected_bcc_groups.push(group)
      this.bcc_active = true
    },
    removeGroupFromAllLists(group) {
      // A helper method to remove the group from all lists before adding to the new one
      this.selected_groups = this.selected_groups.filter((g) => g.id !== group.id)
      this.selected_cc_groups = this.selected_cc_groups.filter((g) => g.id !== group.id)
      this.selected_bcc_groups = this.selected_bcc_groups.filter((g) => g.id !== group.id)
    },

    deleteCCCompany(company) {
      let index = this.selected_cc_companies.findIndex((x) => x == company)
      this.selected_cc_companies.splice(index, 1)
    },

    deleteBCCCompany(company) {
      let index = this.selected_bcc_companies.findIndex((x) => x == company)
      this.selected_bcc_companies.splice(index, 1)
    },

    removeCCContact(contact) {
      let index = this.selected_cc_contacts.findIndex((x) => x == contact)
      this.selected_cc_contacts.splice(index, 1)
    },

    removeBCCContact(contact) {
      let index = this.selected_bcc_contacts.findIndex((x) => x == contact)
      this.selected_bcc_contacts.splice(index, 1)
    }
  }
}
</script>
<style scoped>
input[type="file"] {
  display: none;
}
</style>
